import React, { useState, useRef } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite-no-reset.min.css";
import { UserSegmentEnum } from "../../types";
import Button from "../../componentLibrary/Button";
import Input from "../../componentLibrary/Input";
import Modal from "../../componentLibrary/Modal";
import Combobox, { ComboboxOptionI } from "../../componentLibrary/Combobox";
import { parseInputFieldValue } from "../CreateLessonPage/utils/dateTimeUtils";
import { DateTimeTypeEnum } from "../../types";

interface DatePickerRef {
  root?: {
    querySelector?: (selector: string) => HTMLElement | null;
  };
}

interface CreateAccessCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    code: string;
    limitCount?: number;
    segment: string;
    expirationDate?: Date;
  }) => void;
}

const CreateAccessCodeModal: React.FC<CreateAccessCodeModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const segmentOptions: ComboboxOptionI[] = [
    { value: UserSegmentEnum.NO_SEGMENT, label: "No Segment" },
    { value: UserSegmentEnum.TEAM, label: "Team" },
    { value: UserSegmentEnum.BETA_TESTERS, label: "Beta Testers" },
    { value: UserSegmentEnum.ADVISORY_BOARD, label: "Advisory Board" },
    { value: UserSegmentEnum.EVALUATOR, label: "Evaluator" },
    { value: UserSegmentEnum.TEST_ACCOUNT, label: "Test Account" },
    { value: UserSegmentEnum.ASU_CALM, label: "ASU CALM" },
    { value: UserSegmentEnum.CBIZ, label: "CBIZ" },
    { value: UserSegmentEnum.ASU, label: "ASU" },
    { value: UserSegmentEnum.USER_GROUP_A, label: "User Group A" },
    { value: UserSegmentEnum.USER_GROUP_B, label: "User Group B" },
    { value: UserSegmentEnum.FRIENDS_AND_FAMILY, label: "Friends and Family" },
    { value: UserSegmentEnum.NO_DATA, label: "No Data" },
  ];

  const [code, setCode] = useState("");
  const [limitCount, setLimitCount] = useState("");
  const [segment, setSegment] = useState<ComboboxOptionI>(segmentOptions[0]);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [errors, setErrors] = useState<{
    code?: string;
    limitCount?: string;
    segment?: string;
    expirationDate?: string;
  }>({});
  const datePickerRef = useRef<DatePickerRef | null>(null);
  const pendingCalendarValue = useRef<Date | null>(null);

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!code.trim()) {
      newErrors.code = "Access code is required";
    } else if (code.length < 3) {
      newErrors.code = "Access code must be at least 3 characters";
    }

    if (limitCount) {
      const parsedLimit = parseInt(limitCount);
      if (isNaN(parsedLimit)) {
        newErrors.limitCount = "Limit count must be a valid number";
      } else if (parsedLimit <= 0) {
        newErrors.limitCount = "Limit count must be a positive number";
      }
    }

    if (expirationDate) {
      // Check for Invalid Date - getTime() returns NaN for invalid dates
      if (isNaN(expirationDate.getTime())) {
        newErrors.expirationDate = "Invalid expiration date";
      } else {
        const now = new Date();

        if (expirationDate < now) {
          newErrors.expirationDate = "Expiration date cannot be in the past";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const data: {
      code: string;
      limitCount?: number;
      segment: string;
      expirationDate?: Date;
    } = {
      code: code.trim(),
      segment: segment.value,
    };

    if (limitCount) {
      data.limitCount = parseInt(limitCount);
    }

    if (expirationDate && !isNaN(expirationDate.getTime())) {
      // If the time is exactly midnight (00:00:00), default to end of day (23:59:59)
      const dateToSave = new Date(expirationDate);
      if (
        dateToSave.getHours() === 0 &&
        dateToSave.getMinutes() === 0 &&
        dateToSave.getSeconds() === 0
      ) {
        dateToSave.setHours(23, 59, 59, 999);
      }
      data.expirationDate = dateToSave;
    }

    onSave(data);
    handleClose();
  };

  const handleClose = () => {
    setCode("");
    setLimitCount("");
    setSegment(segmentOptions[0]);
    setExpirationDate(null);
    setErrors({});
    onClose();
  };

  // Handler to change expiration date (called when calendar changes)
  const handleExpirationDateChange = (date: Date | null) => {
    // Track pending calendar selection
    pendingCalendarValue.current = date;
  };

  // Handler when the date picker closes
  const handleDatePickerClose = () => {
    // First, try to parse input field value (handles dates without time)
    let dateToSet: Date | null = null;

    if (datePickerRef.current) {
      const inputElement = datePickerRef.current?.root?.querySelector(
        "input",
      ) as HTMLInputElement;
      if (inputElement?.value) {
        const inputValue = inputElement.value.trim();
        const parsed = parseInputFieldValue(
          inputValue,
          DateTimeTypeEnum.DATE_TIME,
        );
        if (parsed.isValid && parsed.value) {
          dateToSet = parsed.value;

          // Check if time was explicitly provided in the input
          // If input doesn't contain time indicators (AM/PM or colon after date), default to 11:59 PM
          const hasTimeComponent = /\d{1,2}:\d{2}|AM|PM/i.test(
            inputValue.split(" ").slice(1).join(" "),
          );

          if (!hasTimeComponent) {
            // No time was provided, default to end of day (11:59 PM)
            dateToSet.setHours(23, 59, 59, 999);
          }
        }
      }
    }

    // If no valid input value, use pending calendar selection
    if (!dateToSet && pendingCalendarValue.current) {
      const calendarValue = pendingCalendarValue.current;
      if (!isNaN(calendarValue.getTime())) {
        dateToSet = new Date(calendarValue);
        // If the time is exactly midnight (00:00:00), default to end of day (23:59:59)
        if (
          dateToSet.getHours() === 0 &&
          dateToSet.getMinutes() === 0 &&
          dateToSet.getSeconds() === 0
        ) {
          dateToSet.setHours(23, 59, 59, 999);
        }
      }
    }

    // Update the expiration date state
    if (dateToSet && !isNaN(dateToSet.getTime())) {
      setExpirationDate(dateToSet);
      if (errors.expirationDate) {
        setErrors({ ...errors, expirationDate: undefined });
      }
    } else if (
      pendingCalendarValue.current &&
      isNaN(pendingCalendarValue.current.getTime())
    ) {
      // Only show error if calendar returned invalid date
      setErrors({
        ...errors,
        expirationDate: "Please enter a complete date and time.",
      });
    }

    // Clear pending value
    pendingCalendarValue.current = null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={handleClose}
      title="Create New Access Code"
      size="md"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button onClick={handleClose} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="dark">
            <PlusIcon className="w-5 h-5 mr-2" />
            Create
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Access Code */}
        <div>
          <Input
            label="Access Code"
            required
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (errors.code) setErrors({ ...errors, code: undefined });
            }}
            placeholder="e.g., SUMMER2025"
            className="w-full"
          />
          {errors.code && (
            <p className="text-red-500 text-sm mt-1">{errors.code}</p>
          )}
        </div>

        <div>
          <Combobox
            label="Segment"
            options={segmentOptions}
            value={segment}
            onChange={(selectedOption) => setSegment(selectedOption)}
            placeholder="Select a segment"
          />
        </div>

        <div>
          <Input
            label="Limit Count"
            type="number"
            value={limitCount}
            onChange={(e) => {
              setLimitCount(e.target.value);
              if (errors.limitCount)
                setErrors({ ...errors, limitCount: undefined });
            }}
            placeholder="Leave empty for unlimited"
            className="w-full"
            min="1"
          />
          {errors.limitCount && (
            <p className="text-red-500 text-sm mt-1">{errors.limitCount}</p>
          )}
          <p className="text-gray-500 text-sm mt-1">
            Maximum number of times this code can be redeemed
          </p>
        </div>

        <div>
          <label className="block text-sm font-bold mb-2 text-gray-700">
            Expiration Date
          </label>
          <DatePicker
            ref={datePickerRef as React.Ref<HTMLDivElement>}
            value={expirationDate}
            onChange={handleExpirationDateChange}
            onClose={handleDatePickerClose}
            onClean={() => {
              setExpirationDate(null);
              if (errors.expirationDate) {
                setErrors({ ...errors, expirationDate: undefined });
              }
            }}
            format="MM/dd/yyyy hh:mm aa"
            cleanable={true}
            placeholder="No expiration"
            showMeridian
            placement="topStart"
            className="w-full"
            style={{ width: "100%", zIndex: 9999 }}
            menuStyle={{ zIndex: 9999 }}
          />
          {errors.expirationDate && (
            <p className="text-red-500 text-sm mt-1">{errors.expirationDate}</p>
          )}
          <p className="text-gray-500 text-sm mt-1">
            Leave empty for no expiration. Time defaults to 11:59 PM if not
            specified.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default CreateAccessCodeModal;
